import React, { useEffect, useState, useMemo, useRef } from 'react';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import moment from 'moment';
import HeatMapTable, {
  SortItem,
  creatHeatColumns,
  HeatMapTableColumn
} from '@/pages/dashboard/components/HeatMapTable';
import MetricAndUpDown from '@/pages/dashboard/components/MetricAndUpDown';
import DownloadFormatModal from "@/pages/dashboard/components/DownloadFormatModal";

import { getCountryFlag } from "@/pages/home/_config/countries";
import {
  formatAccumulation,
  formatFilterValues,
  formatNum,
  formatPercentNum, generateCopyData, getDynamicLocalCnEnText,
  getUpAndDownStatus, getWeekDay,
  timeZoneTransformForShow,
} from '@/pages/dashboard/utils/utils';

import {
  HEAT_COLUMN_KEYS,
  newUsersTimezoneTextLocalConfig,
  textLocalConfig
} from '@/pages/dashboard/pages/multiTimezone/_config/constant';
import { useLocalOptions, useWeekDayArr, useLocalText, useSelectArrObj } from '@/pages/dashboard/utils/customHook';
import { getAccumulateOptionsWithUTC } from "@/pages/dashboard/pages/multiTimezone/_config/utils";
import { LANGUAGE_MAP, PERIODTIMEOBJ } from "@/pages/dashboard/_config/constant";
import useNewUsersRetentionHeatTableModel from "@/pages/dashboard/servicesModels/multiTimezone/newUsersRetentionHeatTableModel";
import { downLoadNewUsersRetentionByTimezoneTable } from "@/pages/dashboard/services/multiTimezone";
import MetricsSettings from "@/pages/dashboard/components/MetricsSettings";
import DimensionAccumulateRow from '@/pages/dashboard/components/DimensionAccumulateRow';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';



/**
 * 传入参数
 */
interface Props {
  gameCode: string;
  formValue: any; // 顶部筛选结果
  tag: string; // 宽表类型
  filterLoading: boolean;
  selectLabel?: string;
  timezoneType: string;
}


export default function HeatTable(props: Props) {
  const { gameCode, formValue, filterLoading, timezoneType } = props;
  const { selector } = useSelector((state: any) => state?.dashboardSetting ?? {}, shallowEqual);
  const { formatMessage } = useIntl();
  const local = getLocale();
  const localText = useLocalText([...textLocalConfig, ...newUsersTimezoneTextLocalConfig]);
  const weekDayArr = useWeekDayArr();
  const timezoneTypeRef = useRef(timezoneType);

  // 下载设置弹框
  const [visible, setVisible] = useState<Boolean>(false);

  const [accumulated, setAccumulated] = useState<string[]>(['date', timezoneType]);
  const accumulatedRef = useRef(['date', timezoneType]);
  const accumulatedCloseRef = useRef(['date', timezoneType]);


  const [isDownloading, setIsDownLoading] = useState(false);
  // 排序值
  const [sortValue, setSortValue] = useState<SortItem[]>([]);
  // 表格分页记录
  const [tablePaging, setTablePaging] = useState({ pageIndex: 1, pageSize: 10 });
  const [total, setTotal] = useState(0);

  const [columns, setColumns] = useState<HeatMapTableColumn[]>([]);

  // ShowMoreStatistics 下拉多选 相关
  const [showValue, setShowValue] = useState(['avg']);

  // 下载配置中的指标
  const metricOptions = useMemo(() => {
    const opts = HEAT_COLUMN_KEYS.map((key) => ({
      value: key,
      text: formatMessage({ id: `engagement_retention_d` }, { num: key.slice(1, key.length) }),
    }));
    opts.unshift({ value: "register_users", text: localText.register });
    return opts;
  }, [local]);
  const [metricsValue, setMetricsValue] = useState(metricOptions?.map(opt => opt?.value) || []);

  const { componentData, loading, error, getData, overLimitBarTriggerKey } = useNewUsersRetentionHeatTableModel(props, formValue, filterLoading, accumulated, tablePaging, sortValue, timezoneType);

  useEffect(() => {
    if (!loading && (!error)) {
      setColumns(getColumns());
      setTotal(componentData?.row_count || 0);
    }
  }, [componentData, loading]);


  useEffect(() => {
    if (timezoneTypeRef.current !== timezoneType) {
      setAccumulated(['date', timezoneType]);
      setShowValue(['avg']);
      timezoneTypeRef.current = timezoneType;
    }

    setTablePaging({ pageIndex: 1, pageSize: 10 });
    setSortValue([]);
    setTotal(0);
    setColumns([]);

  }, [timezoneType, formValue]);

  useEffect(() => {
    setColumns(getColumns());
  }, [local, metricsValue]);

  const hasDate = accumulated?.includes('date');
  const isAccumulated = !hasDate;

  // 计算列
  const getColumns = (): HeatMapTableColumn => {

    // accumulate列配置
    const selectHeader = {
      "zone": formatMessage({ id: 'common_Zone' }),
      "region": formatMessage({ id: 'common_Region' }),
      "country": formatMessage({ id: 'common_Country' }),
      "system": formatMessage({ id: 'common_System' }),
      "channel": formatMessage({ id: 'common_Channel' }),
      "language": formatMessage({ id: 'common_Language' }),
      "bundle": formatMessage({ id: 'common_Bundle' }),
    };
    const timeZoneColumn = {
      code: timezoneType,
      width: 235,
      align: 'left',
      features: { sortable: true },
      name: localText.timezone,
      render: (record) => {
        let text = record?.text;
        const isHaveTwoQuote = record?.text?.split("(")?.length > 2;
        const isHaveQuote = record?.text?.includes('UTC') || record?.text?.includes('utc') || record?.text?.includes('Utc');
        if (isHaveQuote) {
          if (isHaveTwoQuote) {
            const strArr = record?.text?.split("(");
            const name = `${strArr?.[0]}(${strArr?.[1]}`;
            const utc = strArr?.[2];
            const localText = getDynamicLocalCnEnText(timezoneType, record?.value, gameCode, local, selector);
            text = utc ? timeZoneTransformForShow(`${localText}(${utc}`) : name;
          } else {
            const [name = "-", utc] = record?.text?.split("(") || [];
            const localText = getDynamicLocalCnEnText(timezoneType, record?.value, gameCode, local, selector);
            text = utc ? timeZoneTransformForShow(`${localText}(${utc}`) : name;
          }
        } else {
          const localText = getDynamicLocalCnEnText(timezoneType, record?.value, gameCode, local, selector);
          text = timeZoneTransformForShow(localText);
        }

        return (<div className="dbd-table-cell-nowrap">
          {
            timezoneType === "country" && record?.value &&
            <img className="dbd-heat-table-country-icon" src={getCountryFlag(record?.value)} alt={text} />
          }
          <span className='dbd-table-cell-ellipsis' title={text}>{text}</span>
        </div>
        );
      }
    };
    const accumulateValues = accumulated;// .slice(1, accumulated.length);
    const accumulateColumns = [];
    accumulateValues.forEach((val) => {
      if (val === timezoneType) return; // 上报时区的accumulate的数据是在TimeZone列展示.
      if (val === 'date') return;
      accumulateColumns.push({
        code: val,
        width: 150,
        align: 'center',
        title: selectHeader[val],
        features: { sortable: true },
        render: (record) => {
          const localText = record?.value ? getDynamicLocalCnEnText(val, record?.value, gameCode, local, selector) : record?.text;
          return (<span className='dbd-table-cell-ellipsis' title={localText}>{localText}</span>);
        }
      });
    });
    accumulateValues.includes(timezoneType) && accumulateColumns.unshift(timeZoneColumn);

    const DATE_COLUMN = {
      code: 'date',
      width: isAccumulated ? 210 : 160,
      align: 'left',
      lock: true,
      features: { sortable: true },
      name: formatMessage({ id: 'engagement_retention_date' }),
      render(date) {
        // if (moment(date).toDate() == "Invalid Date") return date;
        const weekDay = getWeekDay(date, weekDayArr)?.slice(1, -1);
        const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
        return isAccumulated || (moment(date).toDate() == "Invalid Date") ? date :
          <>
            <span className='table-text'>{moment(date).format("YYYY-MM-DD")}</span><span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>
          </>;
      }
    };
    // register列配置
    const REGISTER_COLUMN: HeatMapTableColumn = {
      code: 'register_users',
      width: 160,
      title: localText.register,
      align: 'center',
      features: { sortable: true },
      render: ({ count, dod, status }) => {
        status = getUpAndDownStatus(dod, !!status);
        return (
          <MetricAndUpDown
            metric={count}
            updown={{ data: formatPercentNum(parseFloat(dod), 1, true), status }}
            hasTooltip={false}
          />
        );
      },
    };

    // 判断用户是否勾选了Register列
    const isHaveRegister = metricsValue[0] === "register_users" ? 1 : 0;

    // 热力值列
    const heatColumns = metricsValue.slice(isHaveRegister, metricsValue.length).map((key) => ({
      code: key,
      title: formatMessage({ id: `engagement_retention_d` }, { num: key.slice(1, key.length) }),
      width: 100,
    }));

    const newColumn = [DATE_COLUMN, ...accumulateColumns];
    isHaveRegister && newColumn.push(REGISTER_COLUMN);

    return newColumn.concat(creatHeatColumns(heatColumns));
  };


  // 计算热力图显示颜色的程度
  const heatCellColorPercent = (percentageStr) => {
    const floatNum = parseFloat(percentageStr);
    if (isNaN(floatNum)) return 0;
    if (floatNum >= 0 && floatNum <= 0.1) return 0.05;
    if (floatNum > 0.1 && floatNum <= 0.2) return 0.1;
    if (floatNum > 0.2 && floatNum <= 0.3) return 0.2;
    if (floatNum > 0.3 && floatNum <= 0.4) return 0.3;
    if (floatNum > 0.4 && floatNum <= 0.5) return 0.4;
    if (floatNum > 0.5 && floatNum <= 0.6) return 0.5;
    if (floatNum > 0.6 && floatNum <= 0.7) return 0.6;
    if (floatNum > 0.7 && floatNum <= 0.8) return 0.7;
    if (floatNum > 0.8 && floatNum <= 0.9) return 0.8;
    return 0.85;


  };

  /** 返回数据格式整理 */
  const getRecords = (response, isCopy) => {
    const tableData = handleTableData(response?.metrics_list, isCopy);
    if (response?.aggregation?.length > 0) {
      tableData.push(...handleAvgData(response?.aggregation, isCopy));
    }
    return tableData;
  };
  const handleTableData = (data: any[] = [], isCopy) => {
    if (isCopy) data = generateCopyData(data, { gameCode, selectorList: selector });
    return data?.map(({ date, metrics = {}, ...accumulateValue } = {}) => {
      //
      const metricsObj = {};
      HEAT_COLUMN_KEYS.forEach((key) => {
        const cellContent = isCopy ? metrics[key]?.value : formatPercentNum(metrics[key]?.value);
        metricsObj[key] = {
          bubble: {
            content: (<>
              <span
                className="table-bubble-date">{date === 'Average' ? formatMessage({ id: 'engagement_retention_average' }) : date}</span>
              <div className="table-bubble-info">
                <span className="Revenue-table-Lspan">{formatMessage({ id: 'engagement_retention_user' })}：</span>
                <span className="Revenue-table-Rspan">{formatNum(dataFormat({ value: metrics[`active_users_${key}`]?.value, formatRule: 'thousandsInit' }))}</span>
              </div>
              <div className="table-bubble-info">
                <span className="Revenue-table-Lspan">{formatMessage({ id: 'engagement_retention_rate' })}：</span>
                <span className="Revenue-table-Rspan">{cellContent}</span>
              </div>
            </>)
          },
          cellContent,
          colorPercent: heatCellColorPercent(metrics[key]?.value),
        };
      });

      // 计算 Accumulate筛选器的动态列的数据
      const accumulatedData = {};
      accumulated.forEach((value) => {
        accumulatedData[value] = {
          value: accumulateValue[value] === "not_found" ? "default" : accumulateValue[value],
          text: accumulateValue[`${value}_name`] || "-"
        };
      });

      return {
        register_users: {
          dod: metrics?.register_users?.dod,
          count: isCopy ? metrics?.register_users?.value : formatNum(metrics?.register_users?.value, false, true),
        },
        ...accumulatedData,
        ...metricsObj,
        date
      };
    });
  };
  // 底部聚合 数据格式整理
  const handleAvgData = (aggregation = [], isCopy) => {
    const avgDatas = [];
    aggregation?.forEach?.((avgData) => {
      const { date, metrics = {}, type, ...accumulateValue } = avgData ?? {};
      if (!showValue.includes(type)) return;
      // 计算 Accumulate筛选器的动态列的数据
      const accumulatedData = {};
      accumulated.map((value) => {
        accumulatedData[value] = { value: accumulateValue[value], text: accumulateValue[`${value}_name`] || "-" };
      });

      const avgs = {};
      HEAT_COLUMN_KEYS?.forEach((key) => {
        avgs[key] = {
          bubble: { visible: false },
          cellContent: type === "count" ? (isCopy ? metrics[key]?.value : formatNum(metrics[key]?.value, false, true)) : (isCopy ? metrics[key]?.value : formatPercentNum(metrics[key]?.value)),
          colorPercent: type === "count" ? 0 : heatCellColorPercent(metrics[key]?.value),
        };
      });

      avgDatas.push({
        register_users: {
          dod: metrics?.register_users?.dod,
          count: formatNum(metrics?.register_users?.value, false, true),
          status: getUpAndDownStatus(null, true),
        },
        ...accumulatedData,
        ...avgs,
        date: localText[type],
      });
    });
    return avgDatas;
  };

  /**
   * 下载文件
   */
  const download = () => {
    if (isDownloading) return; // 下载时，不可再次点击
    setVisible(true);
  };
  const downloadConfirm = (downloadFormat) => {

    setIsDownLoading(true);
    /**
     * 获取上传参数对象
     */
    const getTableReqParams = () => {
      let desc = true;
      const orderBy = sortValue.map((value) => {
        desc = value.order === 'desc';
        return value.code;
      });
      const { rangeTime, timeType, diff, ...filters } = formValue;
      return {
        game_code: gameCode,
        start_time: formValue.rangeTime[0],
        end_time: formValue.rangeTime[1],
        period: PERIODTIMEOBJ?.Day,
        timezone_type: timezoneType,
        retention_type: "new",
        filters: formatFilterValues(filters),
        accumulation: formatAccumulation(accumulated),
        desc,
        sorted_by: orderBy.length ? orderBy[0] : 'date',
        page: tablePaging.pageIndex,
        page_size: tablePaging.pageSize
      };
    };
    const params = getTableReqParams();
    params.metrics = downloadFormat.metrics;
    params.show_dod = false;
    params.file_format = downloadFormat.fileFormat;
    params.aggregates = downloadFormat.isDownloadStatistics ? showValue : [];

    return downLoadNewUsersRetentionByTimezoneTable(params, LANGUAGE_MAP[getLocale()]).finally(() => { setIsDownLoading(false) });
  };


  // Accumulate 下拉选的选项
  let SEPARATED_OPTIONS = useSelectArrObj(formValue);
  SEPARATED_OPTIONS = SEPARATED_OPTIONS?.filter((i: { groupKey: string; }) => i?.groupKey === 'separated');
  const options = useMemo(() => getAccumulateOptionsWithUTC(SEPARATED_OPTIONS, timezoneType, `(${localText.timezone})`)
    , [timezoneType, getLocale(), formValue]);

  const records = useMemo(() => getRecords(componentData, false), [componentData, showValue]);
  const copyData = useMemo(() => getRecords(componentData, true), [componentData, showValue, getLocale()]);
  const statisticsTypeOptions = useLocalOptions(["avg", "max", "min", "count"]);

  const onSortChange = (sorts: SortItem[] = []) => {
    setSortValue(sorts);
    getData(formValue, accumulated, tablePaging, sorts);
  };

  const onPagingChange = (pagingQuery: React.SetStateAction<{ pageIndex: number; pageSize: number; }>) => {
    setTablePaging(pagingQuery);
    getData(formValue, accumulated, pagingQuery, sortValue);
  };

  return (
    <>
      <HeatMapTable
        timeType='Day'
        operation={(<MetricsSettings
          onConfirm={({ metricValues, statisticsType }) => {
            setShowValue(statisticsType);
            setMetricsValue(metricValues);
          }}
          disabledOptions={[]}
          metricOptions={metricOptions}
          statisticsTypeOptions={statisticsTypeOptions}
          defaultStatisticsType={["avg"]}
        />)}
        title={localText.title}
        records={records}
        copyData={copyData}
        copyConfig={{
          isHaveWeek: accumulated?.includes('date')
        }}
        defaultColumnWidth={50}
        columns={columns}
        sorting={{
          sorts: sortValue,
          onChangeSorts: onSortChange
        }}
        paging={{
          recordCount: total,
          pageSize: tablePaging.pageSize,
          pageIndex: tablePaging.pageIndex,
          onPagingChange
        }}
        loading={loading}
        error={error}
        onDownload={download}
        frontLogicalFlag={componentData?.front_logical_flag}
        overLimitBarTriggerKey={overLimitBarTriggerKey}
        topTip={
          {
            left: (<>
              <DimensionAccumulateRow
                value={accumulated}
                options={options}
                onChange={(value) => {
                  setAccumulated(value);
                  const flag = accumulatedCloseRef.current?.toString() !== value.current?.toString();
                  if (flag) {
                    accumulatedRef.current = value;
                    accumulatedCloseRef.current = value;
                    setTablePaging({ pageIndex: 1, pageSize: 10 });
                    setSortValue([]);
                  }
                }}
              />
            </>)
          }
        }

      />
      <DownloadFormatModal
        tableName={localText.title}
        visible={visible}
        onConfirm={(value) => { downloadConfirm(value) }}
        onCancel={() => setVisible(false)}
        metricOptions={metricOptions}
        timeType={'Day'}
        showData={{ valueOnly: true, showDod: false, showSdlw: false }}
      />
    </>
  );
}
